// @flow
import * as React from 'react';
import { MapContext } from 'esm-web-pro/containers/map/context';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import { isEmpty } from 'lodash';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import CableAttributes from 'esm-web-pro/containers/pages/network/CableAttributes';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import { Box } from 'grommet';
import type { UUID } from 'esm-web-pro/types/common';
import CreateCableVisualisation from './CreateCableVisualisation';
import CableForm from './CableForm';


type Props = {
    formData: Object,
    onClose: Function,
    provider: Object,
    object: Object,
    formDisplayValues: Object,
    getLines: (
        mapUuid: string,
        startEndPointsCoordinates: Array<[number, number]>,
        cableStage: string,
    ) => Promise<Object>,
    createCable: (map: Object, data: Object) => Promise<Object>,
    allowedForkIdentities?: UUID[],
    connectToForks: Function => Promise<UUID[]>,
};

export type PipeSegment = {
    name: string, // pipe uuid
    path: Array<[number, number]>,
};

export type Line = {
    startNumber?: number,
    endNumber?: number,
    options?: {
        [string]: { // line uuid
            name: string,
            pipes?: {
                [string]: PipeSegment,
                path: Array<[number, number]>,
            },
        }
    },
    value?: ?string,
    path: Array<[number, number]>,
    continuousPipe?: boolean,
};

export type Points = Array<{
    number: number,
    layer?: Object,
    reserve?: number,
    continuousPipe?: boolean,
    coordinates: [number, number],
    color: string,
    provider: string,
    type?: string,
}>;

export type Lines = Array<Line>;

const getDefaultChoice = (choices) => {
    if (Object.keys(choices).length === 1) {
        return Object.keys(choices)[0];
    }

    const emptyPipe = Object.keys(choices)
        .find((key) => choices[key].is_empty) || null;

    if (emptyPipe?.pipes) {
        return emptyPipe.pipes[0];
    }

    return emptyPipe || null;
};

const CreateCable = (props: Props) => {
    const {
        onClose, formData, provider, object, formDisplayValues, getLines, createCable,
        connectToForks, allowedForkIdentities,
    } = props;
    const [points: Points, setPoints] = React.useState([]);
    const [lines: Lines, setLines] = React.useState([]);

    const map = React.useContext(MapContext);
    const prevLines = usePrevious(lines);
    const prevPoints = usePrevious(points);

    const [submitting, setSubmitting] = React.useState(false);
    /*
     * Effect to fetch the lines through which a cable can go between the last two drawn points
     * If there is only one option assign in automatically
     */
    React.useEffect(() => {
        if (prevLines && lines.length - prevLines?.length === 1) {
            const lastLine = lines[lines.length - 1];
            if (lastLine.options || lastLine.continuousPipe) return;

            const path = lastLine.path.map((c) => [c[1], c[0]]);
            getLines(map.uuid, path, formData.stage)
                .then((response) => {
                    const newLines = [...lines];
                    newLines[newLines.length - 1] = {
                        ...lastLine,
                        options: response.results.choices,
                        value: getDefaultChoice(response.results.choices),
                    };

                    setLines(newLines);
                });
        }
    }, [lines, prevLines, getLines, map.uuid, formData.stage]);

    /*
     * Calculate points and lines numbers
     */
    React.useEffect(() => {
        if (points?.length === prevPoints?.length) return;

        const newPoints = [...points];
        const newLines = [...lines];

        let pipePointCount = 0;
        let segmentPointCount = 0;
        let pointNumber;

        newPoints.forEach((coordinate, index) => {
            if (index < points.length && points[index].continuousPipe) {
                segmentPointCount += 1;
                pointNumber = Number(`${pipePointCount}.${segmentPointCount}`);
            } else {
                pipePointCount += 1;
                pointNumber = pipePointCount;
                segmentPointCount = 0;
            }

            newPoints[index].pointNumber = pointNumber;

            if (index < newLines.length - 1) {
                newLines[index].startNumber = pointNumber;
                if (index > 0) {
                    newLines[index - 1].endNumber = pointNumber;
                }
            }
        });

        if (!isEmpty(newLines) && pointNumber) {
            newLines[newLines.length - 1].startNumber = newPoints[newPoints.length - 2].pointNumber;
            newLines[newLines.length - 1].endNumber = newPoints[newPoints.length - 1].continuousPipe
                ? pointNumber + 1 : pointNumber;
        }
        setLines(newLines);
        setPoints(newPoints);
    }, [points, lines]);

    /*
     * Effect to check possible connections between forks and the last point
     */
    React.useEffect(() => {
        if (prevPoints?.length !== points.length && points.length > 0) {
            const point = points[points.length - 1];
            const uuid = point.layer ? point.layer.feature.properties.point.uuid : point.uuid;
            connectToForks(uuid, { map: map.uuid });
        }
    }, [points]);

    const handleLineChange = React.useCallback((index, choice) => {
        const newLines: Lines = [...lines];
        const newPoints: Points = [...points];

        newLines[index].value = choice;
        // If the last line is a continuous pipe remove all its segments and points
        while (newPoints[newPoints.length - 2].continuousPipe) {
            newPoints.pop();
            newLines.pop();
        }
        // Mark the last as not part of continuous pipe if after removing points it turns out that previously
        // it was a point from a continuous pipe
        newPoints[newPoints.length - 1].continuousPipe = false;
        newPoints[newPoints.length - 1].pointNumber = points[points.length - 1].pointNumber;
        setPoints(newPoints);
        setLines(newLines);
    }, [lines, points]);

    const handleReserveChange = React.useCallback((index, reserve) => {
        setPoints((oldPoints) => {
            const newPoints = [...oldPoints];
            newPoints[index].reserve = reserve;
            return newPoints;
        });
    });

    const handleProviderRefChange = React.useCallback((index, providerRef) => {
        setLines((oldLines) => {
            const newLines = [...oldLines];
            newLines[index] = { ...newLines[index], providerRef };
            return newLines;
        });
    }, []);


    const handleContinuousPipe = (continuousLinePoints, continuousLineLines) => {
        // Slice last two points because they are part from the continuous pipe points
        const newPoints = [...points];
        const lastPoint = newPoints.pop();
        continuousLinePoints.slice(0, continuousLinePoints.length - 1).forEach((continuousLinePoint, i) => {
            newPoints.push({ ...continuousLinePoint, continuousPipe: i < continuousLinePoints.length - 1 });
        });
        newPoints.push(
            {
                ...continuousLinePoints[continuousLinePoints.length - 1],
                continuousPipe: false,
                reserve: lastPoint.reserve,
            },
        );

        const newLines = [...lines];

        continuousLineLines.forEach((continuousLineLine) => {
            newLines.push({ ...continuousLineLine, continuousPipe: true });
        });
        setPoints(newPoints);
        setLines(newLines);
    };

    const handlePointClick = React.useCallback((point, line) => {
        if (line) {
            setLines([...lines, line]);
        }

        setPoints([...points, { ...point, reserve: 0, type: point.layer.feature.properties.point.child.type }]);
    }, [points, lines]);

    const handleLastRemove = React.useCallback(() => {
        const newPoints: Points = [...points];
        if (newPoints.length > 1) {
            const newLines: Lines = [...lines];
            while (newPoints[newPoints.length - 2].continuousPipe) {
                newPoints.pop();
                newLines.pop();
            }
            const removedLine = newLines.pop();
            if (removedLine?.layer?.isNew) {
                removedLine.layer.remove();
            }
            setLines(newLines);
        }
        newPoints.pop();
        setPoints(newPoints);
    }, [points, lines]);

    const handleSave = (additionalData) => {
        if (submitting) return;

        const path = lines.map((line, index) => ({
            start_point: {
                type: 'Point',
                coordinates: [points[index].coordinates[1], points[index].coordinates[0]],
            },
            end_point: {
                type: 'Point',
                coordinates: [points[index + 1].coordinates[1], points[index + 1].coordinates[0]],
            },
            line_object: line.value || line.line_object,
            provider_ref: line.providerRef,
        }));

        const reserves = points.map((point) => ({
            coordinates: [point.coordinates[1], point.coordinates[0]],
            infrastructure_provider: provider.id,
            length: point.reserve || 0,
        }));

        setSubmitting(true);
        createCable(map.uuid, {
            ...formData, ...additionalData, infrastructure_provider: provider.id, path, reserves,
        }).then(() => {
            onClose(true);
            setSubmitting(false);
        });
    };

    return (
        <Box height="100%" justify="between">
            <CreateCableVisualisation
                points={points}
                lines={lines}
                onClick={handlePointClick}
                onContinuousPipe={handleContinuousPipe}
                allowedForkIdentities={allowedForkIdentities}
                provider={provider}
            />
            <Box flex={{ shrink: 0 }} pad={{ horizontal: '14px', bottom: 'medium' }}>
                <HorizontalLine color="grey" />
                <CableAttributes
                    objectName={object.name}
                    cableData={formDisplayValues}
                    providerName={provider.entity.name}
                />
                <HorizontalLine color="grey" />
            </Box>
            <CableForm
                points={points}
                lines={lines}
                onLastRemove={handleLastRemove}
                onLineChange={handleLineChange}
                onReserveChange={handleReserveChange}
                onProviderRefChange={handleProviderRefChange}
                onClose={onClose}
                onSave={handleSave}
                disableSave={submitting || (points.length > 0 && points[points.length - 1].type === 'fork')}
                provider={provider}
            />
            <Spinner loading={submitting} />
        </Box>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    getLines: {
        send: (mapUuid, startEndPointsCoordinates, cableStage) => apiClient.resources.line.cable({
            map_uuid: mapUuid,
            start_end_points: startEndPointsCoordinates,
            cable_stage: cableStage,
        }),
    },
    createCable: {
        send: (map, data) => apiClient.resources['cable.geo'].create(map, data),
    },
    connectToForks: {
        send: (uuid: UUID, query: { map: UUID }) => apiClient.resources.point11.connectToForks(uuid, query),
        success: (res) => ({ allowedForkIdentities: res.data }),
    },
});

CreateCable.defaultProps = {
    allowedForkIdentities: [],
};

export default connectAPI(apiCallback)(CreateCable);
